import { useConciergePermissions } from '@/hooks/use-concierge-permissions';

export interface ConciergeOnboardingEnabled {
    isConciergeOnboardingEnabled: boolean;
    /**
     * True once the permission has settled - fetched with its authorization
     * intact, or never fetching - or is held at its last settled value through
     * a masked JWT. While it is false `isConciergeOnboardingEnabled` is not a
     * denial, so redirects must hold rather than branch on it.
     */
    isFetched: boolean;
    /**
     * True only while the permission reflects the circle's current
     * authorization, so it is false while a held value is shown. Gate actions
     * that must not run on a masked circle JWT on this, not on `isFetched`.
     */
    isLive: boolean;
}

/**
 * Keyed on the concierge *create* permission, not read alone: a concierge
 * onboarding workflow creates a concierge plan, while the read permission only
 * grants access to view concierge surfaces. A read-only circle stays on FTUE,
 * and so does a create-only one, since create is not honoured without read.
 * Everything that starts, restarts, abandons, or blocks on a concierge
 * onboarding workflow must branch on this - branching on read instead routes a
 * read-only user into a concierge workflow nothing ever started for them, and
 * leaves the FTUE workflow they actually have unrestartable.
 */
export function useIsConciergeOnboardingEnabled(): ConciergeOnboardingEnabled {
    const { canCreateConcierge, isPermissionsSettled, isPermissionsLive } =
        useConciergePermissions();

    return {
        isConciergeOnboardingEnabled: canCreateConcierge,
        isFetched: isPermissionsSettled,
        isLive: isPermissionsLive,
    };
}
